import { useState } from "react";
import { Search, Loader2, PackageSearch, ExternalLink } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { apiClient, formatApiError } from "@/lib/api";
import { StatusTimeline } from "@/components/StatusTimeline";

const SHIPMENT_STEPS = ["In Transit", "At Port", "Customs Clearance", "Delivered"];
const SHIPMENT_LABELS = {
  "In Transit": "In Transit",
  "At Port": "At Port",
  "Customs Clearance": "Customs Clearance",
  Delivered: "Delivered",
};

const openCarrierTracking = (tracking) => {
  if (!tracking) return;
  if (tracking.method === "post") {
    const form = document.createElement("form");
    form.method = "POST";
    form.action = tracking.action;
    form.target = "_blank";
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = tracking.field_name;
    input.value = tracking.field_value;
    form.appendChild(input);
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  } else {
    window.open(tracking.url, "_blank", "noopener,noreferrer");
  }
};

export const TrackShipmentDialog = ({ open, onOpenChange }) => {
  const [refInput, setRefInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const handleTrack = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await apiClient.get(`/track/${encodeURIComponent(refInput.trim())}`);
      setResult(data);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not find that reference number.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) {
          setRefInput("");
          setResult(null);
          setError("");
        }
      }}
    >
      <DialogContent className="bg-white text-ink max-w-md" data-testid="track-shipment-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <PackageSearch size={18} className="text-gold-muted" />
            Track Your Shipment
          </DialogTitle>
          <DialogDescription data-testid="track-shipment-help-text">
            This looks up Ferguson's internal records only — enter your freight quote reference
            (FQ-XXXXXX), customs filing reference (CF-XXXXXX), or a container number for a
            shipment we have on file for you. This does not track arbitrary external carrier
            container numbers that aren't registered with us.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleTrack} className="flex gap-2">
          <Input
            placeholder="e.g. FQ-AB12CD, CF-AB12CD, or container number"
            value={refInput}
            onChange={(e) => setRefInput(e.target.value.toUpperCase())}
            data-testid="track-reference-input"
            className="bg-cream border-ink/15 text-ink placeholder:text-ink-faint"
            required
          />
          <button
            type="submit"
            disabled={loading || !refInput.trim()}
            data-testid="track-submit-btn"
            className="shrink-0 bg-gold hover:bg-gold-hover disabled:opacity-60 text-[#0A140F] font-display font-semibold text-sm px-4 rounded-md flex items-center justify-center gap-1.5 transition-colors"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={15} />}
            Track
          </button>
        </form>

        {error && (
          <p className="text-sm text-red-600" data-testid="track-error-message">
            {error}
          </p>
        )}

        {result && (
          <div data-testid="track-result-card" className="rounded-xl border border-ink/10 bg-cream p-4 space-y-4">
            <div className="flex items-center justify-between">
              <span className="font-mono text-sm text-ink-soft">{result.reference_number}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full bg-gold/15 text-gold-muted">
                {result.type === "filing" ? "Customs Filing" : result.type === "shipment" ? "Shipment" : "Freight Quote"}
              </span>
            </div>

            {result.type === "shipment" ? (
              <StatusTimeline status={result.status} steps={SHIPMENT_STEPS} labels={SHIPMENT_LABELS} />
            ) : (
              <StatusTimeline status={result.status} />
            )}

            {result.type === "filing" ? (
              <div className="text-sm text-ink">
                <div className="font-medium">{result.company_name}</div>
                <div className="text-xs text-ink-faint mt-1">
                  {result.shipment_direction} &bull; {result.country}
                </div>
              </div>
            ) : result.type === "shipment" ? (
              <div className="text-sm text-ink" data-testid="track-shipment-details">
                <div>
                  {result.origin_port} → {result.destination_port}
                </div>
                <div className="text-xs text-ink-faint mt-1">
                  {result.carrier_name || "Carrier not specified"}
                  {result.container_number && <> &bull; Container {result.container_number}</>}
                  {result.eta && <> &bull; ETA {result.eta}</>}
                </div>
                {result.notes && <div className="text-xs text-ink-faint mt-1">{result.notes}</div>}
                {result.carrier_tracking && (
                  <button
                    onClick={() => openCarrierTracking(result.carrier_tracking)}
                    data-testid="track-carrier-link"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-gold-muted hover:text-gold"
                  >
                    <ExternalLink size={13} /> Track on {result.carrier_tracking.carrier}'s free tracking page
                  </button>
                )}
              </div>
            ) : (
              <div className="text-sm text-ink">
                <div>
                  {result.origin_country} → {result.destination_country}
                </div>
                <div className="text-xs text-ink-faint mt-1">
                  {result.cargo_type} &bull; Est. {result.estimated_shipment_date}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

